import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
} from '@mui/material';
import { FieldProps } from '@rjsf/utils';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { TextFieldElement, useFormContext } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

const BLANK_SENTINEL = '__blank__';

const EnumAttributeSelect = ({
  name,
  options,
  disabled,
}: {
  name: string;
  options: string[];
  disabled?: boolean;
}) => {
  const { control, setValue } = useFormContext();
  const value = useWatch({ control, name });

  return (
    <Select
      data-testid={`select-${name}`}
      value={value ?? BLANK_SENTINEL}
      onChange={(e) => {
        const next = e.target.value === BLANK_SENTINEL ? undefined : e.target.value;
        setValue(name, next, { shouldDirty: true });
      }}
      disabled={disabled}
      size="small"
      fullWidth
    >
      <MenuItem value={BLANK_SENTINEL}>&nbsp;</MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

export const ItemAttributesField = ({ schema: templateSchema }: Pick<FieldProps, 'schema'>) => {
  const { t } = useTranslation();
  const form = useFormContext();

  // Get attributes from JSON schema and sort by x-order
  const attributeFields = useMemo(() => {
    if (typeof templateSchema.properties !== 'object') return [];

    if (templateSchema.type === 'object') {
      const fields = Object.entries(templateSchema.properties).map(
        ([key, property]: [string, any]) => ({
          key,
          name: property?.title ?? key,
          type: property?.type ?? 'string',
          enum: property?.enum,
          unit: property?.['x-unit'] ?? '',
          order: property?.['x-order'] ?? Number.MAX_SAFE_INTEGER, // Use MAX_SAFE_INTEGER for attributes without order
        }),
      );

      // Sort by order field (attributes with x-order first, then others in their original order)
      return fields.sort((a, b) => a.order - b.order);
    }
    return [];
  }, [templateSchema]);

  if (!attributeFields.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        {t('itemTemplates.schemaDetail.noAttributes')}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('itemTemplates.detail.attribute')}</TableCell>
            <TableCell>{t('itemTemplates.detail.value')}</TableCell>
            <TableCell>{t('itemTemplates.schemaDetail.unit')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {attributeFields.map((field) => (
            <TableRow key={field.key}>
              <TableCell>{field.name}</TableCell>
              <TableCell>
                {field.enum ? (
                  <EnumAttributeSelect
                    name={`attributes.${field.key}`}
                    options={field.enum}
                    disabled={form.formState.disabled}
                  />
                ) : field.type === 'number' ? (
                  <TextFieldElement
                    name={`attributes.${field.key}`}
                    label=""
                    disabled={form.formState.disabled}
                    type="number"
                    size="small"
                    fullWidth
                  />
                ) : (
                  <TextFieldElement
                    name={`attributes.${field.key}`}
                    label=""
                    disabled={form.formState.disabled}
                    size="small"
                    fullWidth
                  />
                )}
              </TableCell>
              <TableCell>{field.unit}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
